import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { Calendar, AlertTriangle, Download, PackageX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { downloadCsv } from '@/lib/csvExport';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { machineOptionLabel } from '@/lib/machineLabel';
import { parseMachineSelectOptions } from '@/lib/machineCatalog';
import { ProductIdentity } from '@/components/products';

type ScopeType = 'network' | 'hub' | 'spoke' | 'venue' | 'machine';

interface DaysOfCoverItem {
  productId: string;
  productSku: string;
  productName: string;
  productImageUrl: string | null;
  machineStock: number;
  warehouseStock: number;
  currentStock: number;
  avgDailySales: number;
  daysOfCover: number | null;
  reorderLeadDays: number;
  needsReorder: boolean;
  ragStatus: 'green' | 'amber' | 'red' | 'none';
}

interface DaysOfCoverReport {
  period: { startDate: string; endDate: string };
  scope: { scopeType: ScopeType; scopeId: string | null; scopeName: string | null };
  items: DaysOfCoverItem[];
  summary: {
    totalProducts: number;
    needsReorderCount: number;
    criticalCount: number;
    noSalesCount: number;
    averageDaysOfCover: number | null;
  };
}

interface ScopeOption {
  id: string;
  name: string;
}

const SCOPE_LABELS: Record<ScopeType, string> = {
  network: 'Whole network',
  hub: 'Hub',
  spoke: 'Spoke',
  venue: 'Venue',
  machine: 'Machine',
};

function RAGBadge({ status }: { status: DaysOfCoverItem['ragStatus'] }) {
  if (status === 'none') {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
        NO SALES
      </span>
    );
  }
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-200',
    amber: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span className={cn('px-2 py-1 rounded-full text-xs font-medium border', colors[status])}>
      {status.toUpperCase()}
    </span>
  );
}

export default function DaysOfCoverReportPage() {
  const { dateRange, setDateRange } = useDateRange('L30D');
  const [report, setReport] = useState<DaysOfCoverReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNeedsReorder, setShowNeedsReorder] = useState(false);
  const [scopeType, setScopeType] = useState<ScopeType>('network');
  const [scopeId, setScopeId] = useState('');
  const [hubs, setHubs] = useState<ScopeOption[]>([]);
  const [spokes, setSpokes] = useState<ScopeOption[]>([]);
  const [venues, setVenues] = useState<ScopeOption[]>([]);
  const [machines, setMachines] = useState<ScopeOption[]>([]);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/v1/warehouses?pageSize=500'),
      apiFetch('/api/v1/venues?pageSize=500'),
      apiFetch('/api/v1/machines?pageSize=500'),
    ]).then(async ([whRes, venueRes, mcRes]) => {
      if (whRes.ok) {
        const data = await whRes.json();
        const warehouses = (data.items ?? data ?? []) as Array<{
          id: string;
          name: string;
          code?: string;
          type?: string;
        }>;
        setHubs(
          warehouses
            .filter((w) => w.type === 'HUB')
            .map((w) => ({ id: w.id, name: warehouseOptionLabel(w) }))
        );
        setSpokes(
          warehouses
            .filter((w) => w.type === 'SPOKE')
            .map((w) => ({ id: w.id, name: warehouseOptionLabel(w) }))
        );
      }
      if (venueRes.ok) {
        const data = await venueRes.json();
        const items = (data.items ?? data ?? []) as Array<{ id: string; name: string }>;
        setVenues(items.map((v) => ({ id: v.id, name: v.name })));
      }
      if (mcRes.ok) {
        const data = await mcRes.json();
        setMachines(
          parseMachineSelectOptions(data).map((machine) => ({
            id: machine.id,
            name: machineOptionLabel(machine),
          }))
        );
      }
    });
  }, []);

  const scopeOptions = useMemo(() => {
    switch (scopeType) {
      case 'hub': return hubs;
      case 'spoke': return spokes;
      case 'venue': return venues;
      case 'machine': return machines;
      default: return [];
    }
  }, [scopeType, hubs, spokes, venues, machines]);

  const scopeReady = scopeType === 'network' || scopeId !== '';

  function handleScopeTypeChange(next: string) {
    setScopeType(next as ScopeType);
    setScopeId('');
  }

  const loadData = useCallback(async () => {
    if (!scopeReady) {
      setReport(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        scopeType,
      });
      if (scopeType !== 'network') params.set('scopeId', scopeId);

      const res = await apiFetch(`/api/v1/reports/days-of-cover?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading days of cover report:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, scopeType, scopeId, scopeReady]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const displayedItems = showNeedsReorder
    ? report?.items.filter((item) => item.needsReorder)
    : report?.items;

  const showWarehouseStock = scopeType === 'network' || scopeType === 'hub' || scopeType === 'spoke';

  function exportCsv() {
    if (!displayedItems || displayedItems.length === 0) return;
    downloadCsv(
      `days-of-cover-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Product',
        'SKU',
        'Machine stock',
        'Warehouse stock',
        'Total stock',
        'Avg daily sales',
        'Days of cover',
        'Lead days',
        'Status',
        'Needs reorder',
      ],
      displayedItems.map((item) => [
        item.productName,
        item.productSku,
        item.machineStock,
        item.warehouseStock,
        item.currentStock,
        item.avgDailySales.toFixed(2),
        item.daysOfCover === null ? 'No sales' : item.daysOfCover.toFixed(1),
        item.reorderLeadDays,
        item.ragStatus === 'none' ? 'NO SALES' : item.ragStatus.toUpperCase(),
        item.needsReorder ? 'Yes' : 'No',
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold">Days of Cover</h1>
          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || !displayedItems || displayedItems.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label>Scope</Label>
                <Select value={scopeType} onValueChange={handleScopeTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SCOPE_LABELS) as ScopeType[]).map((type) => (
                      <SelectItem key={type} value={type}>
                        {SCOPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {scopeType !== 'network' && (
                <div className="space-y-2">
                  <Label>{SCOPE_LABELS[scopeType]}</Label>
                  <Select value={scopeId} onValueChange={setScopeId}>
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${SCOPE_LABELS[scopeType].toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {scopeOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              {scopeType === 'network' &&
                'Stock in all machines plus every hub and spoke warehouse, against sales across the whole estate.'}
              {scopeType === 'hub' &&
                'Stock in this hub, its spokes, and machines they serve, against sales from those machines.'}
              {scopeType === 'spoke' &&
                'Stock in this spoke and the machines it serves, against sales from those machines.'}
              {scopeType === 'venue' &&
                'Stock inside machines at this venue, against sales from those machines.'}
              {scopeType === 'machine' &&
                'Stock inside this machine, against its own sales.'}
            </p>
          </CardContent>
        </Card>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Needs Reorder"
            value={report ? report.summary.needsReorderCount.toString() : '-'}
            icon={AlertTriangle}
            status={report && report.summary.needsReorderCount > 0 ? 'amber' : undefined}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Critical"
            value={report ? report.summary.criticalCount.toString() : '-'}
            icon={AlertTriangle}
            status={report && report.summary.criticalCount > 0 ? 'red' : undefined}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="No Sales in Period"
            value={report ? report.summary.noSalesCount.toString() : '-'}
            icon={PackageX}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Avg Days of Cover"
            value={
              report
                ? report.summary.averageDaysOfCover === null
                  ? 'No sales'
                  : `${report.summary.averageDaysOfCover.toFixed(1)}d`
                : '-'
            }
            icon={Calendar}
            isLoading={loading}
          />
        </MetricGrid>

        <Card>
          <div className="p-4 flex items-center gap-4 border-b">
            <Button
              variant={showNeedsReorder ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowNeedsReorder(!showNeedsReorder)}
            >
              {showNeedsReorder ? 'Show All' : 'Show Needs Reorder Only'}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Machine Stock</TableHead>
                {showWarehouseStock && (
                  <TableHead className="text-right">Warehouse Stock</TableHead>
                )}
                <TableHead className="text-right">Total Stock</TableHead>
                <TableHead className="text-right">Avg Daily Sales</TableHead>
                <TableHead className="text-right">Days of Cover</TableHead>
                <TableHead className="text-right">Lead Days</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reorder</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!scopeReady ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Select a {SCOPE_LABELS[scopeType].toLowerCase()} to run the report
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : displayedItems?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {showNeedsReorder ? 'All products have sufficient stock!' : 'No products found'}
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems?.map((item) => (
                  <TableRow key={item.productId} className={item.ragStatus === 'red' ? 'bg-red-50' : undefined}>
                    <TableCell>
                      <ProductIdentity
                        productId={item.productId}
                        name={item.productName}
                        sku={item.productSku}
                        imageUrl={item.productImageUrl}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell className="text-right">{item.machineStock}</TableCell>
                    {showWarehouseStock && (
                      <TableCell className="text-right">{item.warehouseStock}</TableCell>
                    )}
                    <TableCell className="text-right font-medium">{item.currentStock}</TableCell>
                    <TableCell className="text-right">{item.avgDailySales.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.daysOfCover === null ? (
                        <span className="text-muted-foreground font-normal">—</span>
                      ) : (
                        item.daysOfCover.toFixed(1)
                      )}
                    </TableCell>
                    <TableCell className="text-right">{item.reorderLeadDays}</TableCell>
                    <TableCell>
                      <RAGBadge status={item.ragStatus} />
                    </TableCell>
                    <TableCell>
                      {item.needsReorder ? (
                        <Badge variant="destructive">Reorder</Badge>
                      ) : item.ragStatus === 'none' ? (
                        <span className="text-sm text-muted-foreground">—</span>
                      ) : (
                        <Badge variant="outline">OK</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
